import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import PriceTrendChart from "@/components/public/price-trend-chart"
import type { SeoMeta } from "@/types"
import type { PriceIndexPoint } from "@/types/public-data"

/* ------------------------------------------------------------------ */
/* Raw data shapes from Rails (snake_case keys)                        */
/* ------------------------------------------------------------------ */

interface RawCrime {
  total_crimes: number
  month: string
  categories: Array<{ category: string; count: number }>
}

interface RawPriceIndex {
  price_index: Array<{ date: string; average_price: number }>
  current_average: number | null
}

interface RawAmenities {
  schools: Array<{ name: string; type?: string }>
  parks: Array<{ name: string }>
  restaurants: Array<{ name: string; cuisine?: string }>
  shops: number
  transport_stops: number
  cycleways: number
}

interface RawSoldPrices {
  sold_prices: Array<{ price: number; date: string; street: string }>
}

/* ------------------------------------------------------------------ */
/* Page props                                                          */
/* ------------------------------------------------------------------ */

interface PageProps {
  meta: SeoMeta
  location: string
  cityName: string
  crime: RawCrime | null
  soldPrices: RawSoldPrices | null
  priceIndex: RawPriceIndex | null
  amenities: RawAmenities | null
  formattedAverage: string | null
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function mapPriceIndex(
  raw: RawPriceIndex["price_index"] | undefined
): PriceIndexPoint[] {
  if (!raw) return []
  return raw.map((p) => ({ date: p.date, averagePrice: p.average_price }))
}

function formatPrice(value: number): string {
  return `\u00A3${value.toLocaleString()}`
}

function formatDate(dateString: string): string {
  const d = new Date(dateString)
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

function titleCase(street: string): string {
  return street
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

/* ------------------------------------------------------------------ */
/* Page component                                                      */
/* ------------------------------------------------------------------ */

export default function InsightsLocationPage({
  meta,
  location,
  cityName,
  crime,
  soldPrices,
  priceIndex,
  amenities,
  formattedAverage,
}: PageProps) {
  const priceData = mapPriceIndex(priceIndex?.price_index)
  const recentSales = soldPrices?.sold_prices
    ? [...soldPrices.sold_prices]
        .sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        )
        .slice(0, 8)
    : []

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Head>

      {/* Hero */}
      <section className="bg-cream px-6 py-16 md:py-24">
        <div className="mx-auto max-w-5xl">
          <Link
            href="/insights"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-red transition-colors hover:text-deep-red"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="size-4">
              <path
                fillRule="evenodd"
                d="M17 10a.75.75 0 0 1-.75.75H5.612l4.158 3.96a.75.75 0 1 1-1.04 1.08l-5.5-5.25a.75.75 0 0 1 0-1.08l5.5-5.25a.75.75 0 1 1 1.04 1.08L5.612 9.25H16.25A.75.75 0 0 1 17 10Z"
                clipRule="evenodd"
              />
            </svg>
            All locations
          </Link>

          <h1 className="mt-6 font-display text-3xl font-bold tracking-tight text-charcoal sm:text-4xl md:text-5xl">
            {cityName} property market
            <span className="text-red">.</span>
          </h1>

          {formattedAverage && (
            <p className="mt-4 text-lg text-charcoal">
              Average property price:{" "}
              <span className="font-display font-bold">
                &pound;{formattedAverage}
              </span>
            </p>
          )}
        </div>
      </section>

      {/* Price trend chart */}
      {priceData.length >= 2 && (
        <section className="border-t border-stone px-6 py-12">
          <div className="mx-auto max-w-5xl">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <h2 className="font-display text-xl font-bold text-charcoal">
                  Price trend
                  <span className="text-red">.</span>
                </h2>
                <p className="mt-1 text-sm text-slate">
                  Average property prices over recent months from the UK House
                  Price Index.
                </p>
                <div className="mt-6">
                  <PriceTrendChart data={priceData} height={280} />
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* Context cards */}
      <section className="border-t border-stone bg-cream px-6 py-12">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal stagger className="grid gap-6 sm:grid-cols-3">
            {crime && (
              <div className="rounded-xl border border-stone bg-white p-6">
                <p className="text-xs font-medium uppercase tracking-[0.15em] text-slate">
                  Crime total
                </p>
                <p className="mt-2 font-display text-3xl font-bold text-charcoal">
                  {crime.total_crimes.toLocaleString()}
                </p>
                <p className="mt-1 text-sm text-slate">
                  reported crimes ({crime.month})
                </p>
              </div>
            )}
            {amenities && (
              <div className="rounded-xl border border-stone bg-white p-6">
                <p className="text-xs font-medium uppercase tracking-[0.15em] text-slate">
                  Schools nearby
                </p>
                <p className="mt-2 font-display text-3xl font-bold text-charcoal">
                  {amenities.schools.length}
                </p>
                <p className="mt-1 text-sm text-slate">within 1 mile</p>
              </div>
            )}
            {amenities && (
              <div className="rounded-xl border border-stone bg-white p-6">
                <p className="text-xs font-medium uppercase tracking-[0.15em] text-slate">
                  Parks nearby
                </p>
                <p className="mt-2 font-display text-3xl font-bold text-charcoal">
                  {amenities.parks.length}
                </p>
                <p className="mt-1 text-sm text-slate">within 1 mile</p>
              </div>
            )}
          </ScrollReveal>
        </div>
      </section>

      {/* Recent sales */}
      {recentSales.length > 0 && (
        <section className="border-t border-stone px-6 py-12">
          <div className="mx-auto max-w-5xl">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <h2 className="font-display text-xl font-bold text-charcoal">
                  Recent sales
                  <span className="text-red">.</span>
                </h2>
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-stone text-left text-xs text-slate">
                        <th className="pb-2 font-medium">Street</th>
                        <th className="pb-2 font-medium">Price</th>
                        <th className="pb-2 font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentSales.map((sale, i) => (
                        <tr
                          key={`${sale.street}-${sale.date}-${i}`}
                          className="border-b border-stone/50"
                        >
                          <td className="py-2 text-charcoal">
                            {titleCase(sale.street)}
                          </td>
                          <td className="py-2 font-medium text-charcoal">
                            {formatPrice(sale.price)}
                          </td>
                          <td className="py-2 text-slate">
                            {formatDate(sale.date)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* Data sources */}
      <section className="border-t border-stone px-6 py-8">
        <div className="mx-auto max-w-5xl">
          <p className="text-center text-xs text-slate">
            Data sources: HM Land Registry, UK House Price Index, Police.uk,
            OpenStreetMap (Overpass).
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl">
            Marketing property in {cityName}
            <span className="text-red">?</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            QuiteRight turns your {cityName} listings into scroll-stopping video
            ads in under 60 seconds.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/features"
              className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              See features
            </Link>
            <Link
              href={`/areas/${location}`}
              className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
            >
              {cityName} area guide
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
